// TODO: [FE-51] Improve signup machine typing.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from 'axios'
import 'cross-fetch/polyfill'
import {
  FlowVersion,
  FlowVersionPriorityOrder,
  FlowVersions,
  LighterBasedFlowVersions,
  getCopyNamespaceByFlowVersion,
} from 'flow-version'
import { TreatmentProgram, TreatmentProgramConfig } from 'global.constants'
import i18n from 'i18n'
import { getCountryCallingCode } from 'libphonenumber-js/min'
import { merge, mergeWith } from 'lodash-es'
import {
  AssignAction,
  DoneInvokeEvent,
  EventObject,
  TransitionConfig,
  assign,
  createMachine,
} from 'xstate'
import { pure } from 'xstate/lib/actions'

import {
  AddictionSelectionFormName,
  AddictionSelectionParentAddiction,
} from '~components/AddictionSelectorBasePage/AddictionSelectorBasePage'
import { Country, DataRegion } from '~constants/types'
import {
  logMessage,
  trackAddiction,
  trackCompany,
  trackDownloadLinkVisit,
  trackEmailFilledIn,
  trackFlowVersion,
  trackMinorRegistrationStatus,
  trackOnboardingSelection,
  trackPageView,
  trackPolyAddictions,
  trackPolyaddictionEnabled,
  trackRegistrationComplete,
  trackRejoinPageView,
  trackStateAvailability,
} from '~services/analyticsService/analytics.service'
import companyService, { Company } from '~services/companyService'
import {
  OperatingSystem,
  calculateUserAge,
  doesMeetAgeThresholdForProgramme,
  getCallCopyVariation,
  getIncentive,
  getOperatingSystem,
  isMinorUS,
  isTobaccoLearn,
  onlyAlcoholProgramEnabled,
} from '~utils/helpers'
import { getUpdatedSelectedAddictionAndPriorities } from '~utils/machineHelpers'

import { INITIAL_USER } from './initialUser'
import {
  LighterEvents,
  clearLoginUserData,
  clearUserData,
  updateLighterMemberData,
  updateMotivationData,
} from './lighterActions'
import {
  DownloadFoundEvent,
  SetFlowVersionEvent,
  SignupMachineEvents,
} from './machine-types'
import {
  FlowVersionLocalStorageKey,
  closeWindowIfWebView,
  getFlowVersionFromLocalStorage,
} from './machine.utils'
import {
  confirmNewPassword,
  createLighterAccount,
  createNewUserAccount,
  fetchCompanyEligibility,
  fetchEsiEligibility,
  fetchTreatmentProgramsForState,
  getProgress,
  lighterB2BSave,
  resendPasswordResetEmail,
  saveB2BDetails,
  saveMotivations,
  sendPasswordResetEmail,
  signIn,
  signOut,
  updateUserDetails,
} from './services'
import { submitAddictionSelection } from './services/machineServices'
import { SignupMachineError } from './signup-machine-error'
import { ProgressSignupContext, SignupContext } from './types/signupContext'
import { ELID, OnboardingType } from './types/user'

// States are defined by https://www.figma.com/file/fHEui78iN9Zv0QdCAu9vb4/branch/1kQlwrsebsN4d0dK5zgY6O/Members-Enrolment-Flows?node-id=5112%3A10733

// Hacky typing for the `webkit` property on `window`
declare global {
  interface Window {
    webkit: any
  }
}

export function setFlowVersion<T extends EventObject>(
  flowVersion: FlowVersion,
  log: string
): AssignAction<SignupContext, T> {
  i18n.setDefaultNamespace(getCopyNamespaceByFlowVersion(flowVersion))
  localStorage.setItem(
    FlowVersionLocalStorageKey,
    JSON.stringify({
      flowVersion: flowVersion,
      timestamp: Date.now(),
    })
  )
  trackFlowVersion({ flowVersion, log })
  return assign({
    flowVersion: () => flowVersion,
  }) as unknown as AssignAction<SignupContext, T>
}

interface SetLighterBasedFlowVersionConfig
  extends TransitionConfig<SignupContext, SetFlowVersionEvent> {
  actions: ['setFlowVersion']
  target: '#lighterFlow'
}
/**
 * A shared config used to transition flow versions within
 * {@link LighterBasedFlowVersions} (lighter-based {@link FlowVersion}s) to the
 * lighter flow state after `calculateFlowVersion` (both company initially
 * known and unknown cases).
 */
const LIGHTER_BASED_SET_FLOW_VERSION_CONFIG: SetLighterBasedFlowVersionConfig =
  {
    cond: (_, event: SetFlowVersionEvent) =>
      LighterBasedFlowVersions.includes(event.value.flowVersion),
    actions: ['setFlowVersion'],
    target: '#lighterFlow',
  }

const isUnavailableStateEnabledFeatureSwitch =
  import.meta.env.VITE_ENABLE_UNAVAILABLE_STATES === 'true'
const isRejoinRegFlowEnabledFeatureSwitch =
  import.meta.env.VITE_ENABLE_REJOIN_REG_FLOW === 'true'
const isCallChoiceEnabledFeatureSwitch =
  import.meta.env.VITE_ENABLE_CALL_CHOICE === 'true'
const isAllPlaftormsForCallChoiceEnabledFeatureSwitch =
  import.meta.env.VITE_ENABLE_ALL_PLATFORMS_FOR_CALL_CHOICE === 'true'

const signupMachine = createMachine(
  {
    /** @xstate-layout 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 */
    predictableActionArguments: true,
    tsTypes: {} as import('./signup.machine.typegen').Typegen0,
    schema: {
      context: {} as SignupContext,
      events: {} as
        | { type: 'TRANSITION_CARE_COORDINATOR' }
        // TODO: [GROW-684] Standardise `TRANSITION_ELIGIBILITY_STEP` event to follow same shape as other events in the state machine
        | {
            type: 'TRANSITION_ELIGIBILITY_STEP'
            value: {
              accessCode: string
            }
            data: {
              noMatchingEsiMembers: boolean
            }
          }
        | { type: 'TRANSITION_TOBACCO_SUB_SELECTOR' }
        | { type: 'CALL_BOOKED' }
        | { type: 'COMPANY_CONFIG_FOUND'; value: Company }
        | { type: 'COMPANY_ID_FOUND'; value: number }
        | { type: 'COMPANY_ID_NOT_FOUND' }
        | { type: 'ERROR' }
        | { type: 'FOUND_ESI_MATCH' }
        | { type: 'NO_MATCHING_MEMBERS' }
        | { type: 'REQUEST_ACCESS_CODE' }
        | { type: 'REQUEST_CARRIER_ID' }
        | { type: 'SET_ACCESS_CODE'; value: { accessCode: string } }
        | { type: 'SET_ADDICTION_TYPE'; value: TreatmentProgram }
        | {
            type: 'SET_QUITTING_PRIORITY'
            value: SignupContext['quittingPriority']
          }
        | { type: 'SET_POLY_ADDICTIONS'; value: TreatmentProgram[] }
        | {
            type: 'SET_STATE_UNAVAILABILITY_INFO'
            value: SignupContext['unavailability']['state']
          }
        | { type: 'SET_CARRIER_ID'; value: { carrierId: string } }
        | { type: 'SET_SUB_COMPANY_ID'; value: { subCompanyId: number } }
        | { type: 'SET_COMPANY_ID'; value: { companyId: number } }
        | { type: 'SET_ELID'; value: ELID }
        | { type: 'SET_SIGNUP_DATA'; value: any }
        | { type: 'UPDATE_TREATMENT_PROGRAMS'; value: TreatmentProgramConfig }
        | { type: 'NEXT_STEP' }
        | { type: 'UPDATE_TREATMENT_ACCESS_CODES'; value: TreatmentAccessCode }
        | {
            type: 'EMAIL_FILLED_IN'
            value: {
              email: string
            }
          }
        | LighterEvents
        | SignupMachineEvents,

      services: {} as {
        createNewUserAccount: any
        createLighterAccount: any
        lighterB2BSave: any
        saveMotivations: any
        signOut: any
      },
    },
    on: {
      TRANSITION_CARE_COORDINATOR: '#careCoordinatorCall',
      TRANSITION_ELIGIBILITY_STEP: {
        target: '#eligibilityStep',
        actions: ['assignErrors', 'assignAccessCode'],
      },
      ERROR: '#internalError',
      SET_FLOW_VERSION: {
        actions: ['setFlowVersion'],
      },
      SET_POLY_ADDICTIONS: {
        actions: ['setPolyAddictions', 'trackPolyAddictions'],
      },
    },
    context: {
      flowVersion: FlowVersion.Original,
      availableTreatmentPrograms: {
        alcohol: false,
        cannabis: false,
        chewingTobacco: false,
        cigarettes: false,
        cigarettesLearn: false,
        eCigarettes: false,
        eCigarettesLearn: false,
        opioids: false,
        stimulants: false,
      },
      isAutomaticAddictionSelection: false,
      treatmentAccessCode: {
        alcohol: '',
        opioids: '',
        cigarettes: '',
        eCigarettes: '',
      },
      company: {
        companyId: 0,
        country: Country.US,
        dataRegion: DataRegion.US,
        name: '',
        slug: '',
        multipleLanguages: false,
        requireSouthAfricaIDNumber: false,
        gbEthnicity: false,
        carrierId: '',
        polyaddictionEnabled: false,
        minorsProgramEnabled: false,
        callChoiceEnabled: false,
      },
      motivations: undefined,
      resetPassword: {
        cognitoClientId: '',
        cognitoUserPoolId: '',
        email: '',
        backendErrors: {
          codeMismatch: false,
          invalidPassword: false,
          sthWentWrong: false,
        },
      },
      selectedAddiction: undefined,
      shouldSaveUserProgress: false,
      starterCallSchedulerData: {
        healthieUrl: '',
        metadata: '',
        nylasUrl: '',
      },
      user: INITIAL_USER,
      errors: {
        accessCode: {
          noMatchingEsiMembers: false,
        },
        signIn: {
          loginFailed: false,
        },
        signUp: {
          emailInUse: false,
          passwordLength: false,
        },
        dateOfBirth: {
          isNotMinimumAge: false,
        },
      },
      unavailability: {},
    },
    id: 'signupMachine',
    initial: 'preflight',
    entry: () => sessionStorage.clear(), // clears cognito session from session storage
    states: {
      preflight: {
        description:
          'Check the URL params for a download param and move to download page. Then check for company ID, and fetch the corresponding company config if present',
        initial: 'checkUrlForDocs',
        onDone: 'eligibilityStep',
        tags: 'loading',
        states: {
          checkUrlForDocs: {
            invoke: {
              id: 'checkUrlForDocs',
              src: 'checkUrlForDocs',
            },
            on: {
              TERMS_OF_USE_FOUND: '#docs.termsOfUse',
              PRIVACY_POLICY_FOUND: '#docs.privacyPolicy',
              NOTICE_OF_PRIVACY_PRACTICES_FOUND:
                '#docs.noticeOfPrivacyPractices',
              DOCS_NOT_FOUND: 'checkUrlForDownload',
            },
          },
          checkUrlForDownload: {
            invoke: {
              id: 'checkUrlForDownload',
              src: 'checkUrlForDownload',
            },
            on: {
              DOWNLOAD_FOUND: {
                target: '#directDownload',
              },
              DOWNLOAD_NOT_FOUND: 'checkUrlForLogin',
            },
          },
          checkUrlForLogin: {
            invoke: {
              id: 'checkUrlForLogin',
              src: 'checkUrlForLogin',
            },
            on: {
              LOGIN_FOUND: {
                target: '#login',
              },
              LOGIN_NOT_FOUND: 'checkUrlForCompanyId',
            },
          },
          checkUrlForCompanyId: {
            invoke: {
              id: 'checkUrlForCompanyId',
              src: 'checkUrlForCompanyId',
            },
            on: {
              COMPANY_ID_FOUND: 'fetchCompanyConfig',
              COMPANY_ID_NOT_FOUND: 'calculateFlowVersion',
            },
          },
          fetchCompanyConfig: {
            invoke: {
              id: 'fetchCompanyConfig',
              src: 'fetchCompanyConfig',
            },
            on: {
              COMPANY_CONFIG_FOUND: [
                {
                  cond: 'isCompanyGB',
                  actions: [
                    'assignCompanyConfig',
                    'assignGBUser',
                    'trackPolyaddicitonEnabled',
                  ],
                  target: 'calculateFlowVersion',
                },
                {
                  cond: 'isCompanyZA',
                  actions: [
                    'assignCompanyConfig',
                    'assignZAUser',
                    'trackPolyaddicitonEnabled',
                  ],
                  target: 'calculateFlowVersion',
                },
                {
                  actions: ['assignCompanyConfig', 'trackPolyaddicitonEnabled'],
                  target: 'calculateFlowVersion',
                },
              ],
              ERROR: 'done',
            },
          },
          calculateFlowVersion: {
            invoke: {
              id: 'calculateFlowVersion',
              src: 'calculateFlowVersion',
            },
            on: {
              SET_FLOW_VERSION: [
                LIGHTER_BASED_SET_FLOW_VERSION_CONFIG,
                {
                  actions: ['setFlowVersion'],
                  target: 'done',
                },
              ],
            },
          },
          done: {
            type: 'final',
          },
        },
      },

      docs: {
        id: 'docs',
        type: 'final',
        tags: ['ui'],
        states: {
          termsOfUse: {
            tags: ['doc_termsOfUse'],
          },
          privacyPolicy: {
            tags: ['doc_privacyPolicy'],
          },
          noticeOfPrivacyPractices: {
            tags: ['doc_noticeOfPrivacyPractices'],
          },
        },
      },

      directDownload: {
        id: 'directDownload',
        entry: (_, event) =>
          trackDownloadLinkVisit((event as DownloadFoundEvent).value),
        tags: ['downloadTheApp', 'ui'],
        type: 'final',
      },

      login: {
        tags: ['login', 'ui'],
        id: 'login',
        initial: 'idle',
        entry: () => trackRejoinPageView('accountLogin'),
        states: {
          idle: {
            on: {
              FORGOT_PASSWORD_CLICKED: {
                actions: ['clearLoginFailedError'],
                target: '#resetPasswordFlow',
              },
              LOGIN: {
                actions: ['clearLoginFailedError'],
                target: 'loading',
              },
            },
          },
          loading: {
            tags: 'loading',
            initial: 'signIn',
            states: {
              signIn: {
                invoke: {
                  id: 'signIn',
                  src: 'signIn',
                  onDone: {
                    target: 'checkProgress',
                  },
                  onError: [
                    {
                      target: '#internalError',
                      cond: 'internalErrorCheck',
                    },
                    { actions: ['assignErrors'], target: '#login.idle' },
                  ],
                },
              },
              checkProgress: {
                tags: 'fetchingProgress',
                invoke: {
                  id: 'getProgress',
                  src: 'getProgress',
                  onDone: [
                    {
                      // Check that the context is for a lighter-based flow and
                      // that the pre account creation steps appear to have
                      // been completed:
                      //  - substance/addiction/quitting
                      //  - first name and last name
                      // If so:
                      //  - assign context
                      //  - log
                      //  - rejoin flow (after account creation)
                      cond: (
                        _,
                        event: DoneInvokeEvent<{
                          context: ProgressSignupContext
                        }>
                      ) =>
                        LighterBasedFlowVersions.includes(
                          event.data.context.flowVersion
                        ) &&
                        !!event.data.context.selectedAddiction &&
                        !!event.data.context.user.firstName &&
                        !!event.data.context.user.lastName,
                      actions: [
                        'assignContext',
                        (_, event) => {
                          logMessage(event.type + ': Rejoining flow')
                        },
                      ],
                      target: '#lighterFlow.lighterPostAccountCreation',
                    },
                    // If the conditions for rejoining the flow aren't met
                    // above:
                    //  - assign context (in case it's useful for call booking)
                    //  - log ( + details)
                    //  - go to the internal error  page (which links to the
                    //    care coordination call booking page)
                    {
                      actions: [
                        'assignContext', // This could pre-fill call booking
                        (
                          _,
                          event: DoneInvokeEvent<{
                            context: ProgressSignupContext
                          }>
                        ) => {
                          const payload = {
                            flowVersion: event.data.context.flowVersion,
                            selectedAddictionInContext:
                              !!event.data.context.selectedAddiction,
                            nameInContext:
                              !!event.data.context.user.firstName &&
                              !!event.data.context.user.lastName,
                          }
                          logMessage(
                            event.type +
                              ': Error: Context misalignment - showing error page',
                            payload
                          )
                        },
                      ],
                      target: '#internalError',
                    },
                  ],
                  onError: [
                    // Check if the error is due to the user having already
                    // completed the flow and if the context has been provided.
                    // If so:
                    //  - assign context (so that the correct final page can be
                    //    shown, for example minors should not see the download
                    //    app page)
                    //  - log
                    //  - go to the final page
                    {
                      cond: (
                        _,
                        event: DoneInvokeEvent<{
                          message: SignupMachineError
                          context?: ProgressSignupContext
                        }>
                      ) =>
                        event.data.message ===
                          SignupMachineError.ProgressGetB2BAlreadySaved &&
                        !!event.data.context,
                      actions: [
                        'assignContext', // Will help decide which final page they should see
                        (_, event) => {
                          logMessage(
                            event.type +
                              ': B2B already saved - showing final page'
                          )
                        },
                      ],
                      target: '#flowDone',
                    },
                    // If the conditions for viewing the final page aren't met
                    // above:
                    //  - assign any context (in case it's useful for call booking)
                    //  - log
                    //  - go to the internal error  page (which links to the
                    //    care coordination call booking page)
                    {
                      actions: [
                        'assignContext', // If there is context, this could pre-fill call booking
                        (_, event) => {
                          logMessage(
                            event.type + ': Error: showing error page',
                            { contextPresent: !!event.data?.context }
                          )
                        },
                      ],
                      target: '#internalError',
                    },
                  ],
                },
              },
            },
          },
        },
      },

      resetPasswordFlow: {
        description: 'Flow for reseting password by member',
        id: 'resetPasswordFlow',
        initial: 'resetPassword',
        exit: 'clearResetPasswordContext',
        states: {
          resetPassword: {
            tags: ['resetPassword', 'ui'],
            id: 'resetPassword',
            entry: () => trackRejoinPageView('resetPasswordRequest'),
            initial: 'idle',
            states: {
              idle: {
                on: {
                  RESET_PASSWORD: {
                    target: 'loading',
                  },
                  RETURN_TO_LOGIN: {
                    target: '#login',
                  },
                },
              },
              loading: {
                tags: 'loading',
                entry: 'clearResetPasswordBackendErrors',
                invoke: {
                  id: 'sendPasswordResetEmail',
                  src: 'sendPasswordResetEmail',
                  onDone: {
                    actions: 'updateResetPasswordContext',
                    target: '#resetPasswordEmailSent',
                  },
                  onError: {
                    target: 'idle',
                    actions: 'assignResetPasswordBackendError',
                  },
                },
              },
            },
          },
          resetPasswordEmailSent: {
            tags: ['resetPasswordEmailSent', 'ui'],
            id: 'resetPasswordEmailSent',
            entry: () => trackRejoinPageView('emailConfirmation'),
            initial: 'idle',
            states: {
              idle: {
                on: {
                  NEXT_STEP: {
                    target: '#resetPasswordNewOne',
                  },
                },
              },
            },
          },
          resetPasswordNewOne: {
            tags: ['resetPasswordNewOne', 'ui'],
            id: 'resetPasswordNewOne',
            entry: () => trackRejoinPageView('updatePassword'),
            initial: 'idle',
            states: {
              idle: {
                on: {
                  CONFIRM_NEW_PASSWORD: {
                    target: 'loading',
                  },
                },
              },
              loading: {
                tags: 'loading',
                entry: 'clearResetPasswordBackendErrors',
                invoke: {
                  id: 'confirmNewPassword',
                  src: 'confirmNewPassword',
                  onDone: { target: '#resetPasswordCompleted' },
                  onError: [
                    {
                      cond: 'isResetPasswordExpiredCodeErrorCheck',
                      target: '#resetPasswordCodeExpired',
                    },
                    {
                      actions: 'assignResetPasswordBackendError',
                      target: 'idle',
                    },
                  ],
                },
              },
            },
          },
          resetPasswordCodeExpired: {
            tags: ['resetPasswordCodeExpired', 'ui'],
            id: 'resetPasswordCodeExpired',
            entry: () => trackRejoinPageView('resetPasswordCodeExpired'),
            initial: 'idle',
            states: {
              idle: {
                on: {
                  RE_RESET_PASSWORD: {
                    target: 'loading',
                  },
                },
              },
              loading: {
                tags: 'loading',
                entry: 'clearResetPasswordBackendErrors',
                invoke: {
                  id: 'resendPasswordResetEmail',
                  src: 'resendPasswordResetEmail',
                  onDone: {
                    target: '#resetPasswordEmailSent',
                  },
                  onError: {
                    actions: 'assignResetPasswordBackendError',
                    target: 'idle',
                  },
                },
              },
            },
          },
          resetPasswordCompleted: {
            tags: ['resetPasswordCompleted', 'ui'],
            id: 'resetPasswordCompleted',
            entry: () => trackRejoinPageView('passwordSuccessfullyUpdated'),
            initial: 'idle',
            states: {
              idle: {
                on: {
                  RETURN_TO_LOGIN: {
                    target: '#login',
                  },
                },
              },
            },
          },
        },
      },
      healthPlanFlow: {
        id: 'healthPlanFlow',
        onDone:
          '#lighterPostAccountCreation.normalLighterPostAccountCreationStep',
        initial: 'insurancePlanDetails',
        entry: 'setShouldSaveUserProgress',
        exit: 'unsetShouldSaveUserProgress',
        states: {
          insurancePlanDetails: {
            tags: ['insurancePlanDetails', 'ui'],
            id: 'insurancePlanDetails',
            entry: () => trackPageView('insurancePlanDetails'),
            on: {
              UPDATE_LIGHTER_MEMBER_DATA: {
                actions: updateLighterMemberData,
                target: 'done',
              },
            },
          },
          done: {
            type: 'final',
          },
        },
      },
      esiFlow: {
        description:
          'Custom eligibility flow for Express Scripts, using the `checkGuestEligibility` endpoint with a combination of `carrierId`, `accessCode` and ELID.',
        id: 'esiFlow',
        initial: 'performEsiEligibilityCheck',
        onDone: '#commonFlow',
        states: {
          accessCode: {
            tags: ['accessCode', 'ui'],
            id: 'accessCode',
            entry: () => trackPageView('accessCode'),
            on: {
              SET_ACCESS_CODE: {
                actions: 'assignAccessCode',
                target: 'performEsiEligibilityCheck',
              },
            },
          },
          esiCarrierSelector: {
            entry: () => trackPageView('esiCarrierSelector'),
            tags: ['esiCarrierSelector', 'ui'],
            id: 'esiCarrierSelector',
            on: {
              SET_CARRIER_ID: {
                actions: 'assignCarrierId',
                target: 'performEsiEligibilityCheck',
              },
              SET_SUB_COMPANY_ID: {
                actions: 'assignSubCompanyId',
                target: 'performEsiEligibilityCheck',
              },
            },
          },
          performEsiEligibilityCheck: {
            tags: 'loading',
            invoke: {
              id: 'performEsiEligibilityCheck',
              src: 'performEsiEligibilityCheck',
            },
            on: {
              FOUND_ESI_MATCH: 'done',
              NO_MATCHING_MEMBERS: {
                target: 'accessCode',
                actions: 'setNoMembersFoundError',
              },
              REQUEST_ACCESS_CODE: 'accessCode',
              REQUEST_CARRIER_ID: 'esiCarrierSelector',
              UPDATE_TREATMENT_PROGRAMS: {
                actions: 'updateAvailableTreatmentPrograms',
              },
              UPDATE_TREATMENT_ACCESS_CODES: {
                actions: 'updateTreatmentAccessCodes',
              },
            },
          },
          done: {
            type: 'final',
          },
        },
      },

      careCoordinatorCall: {
        entry: () => trackPageView('careCoordinatorCall'),
        tags: ['careCoordinatorCall', 'ui'],
        id: 'careCoordinatorCall',
        type: 'final',
      },

      eligibilityStep: {
        tags: ['eligibilityStep', 'ui'],
        id: 'eligibilityStep',
        entry: () => trackPageView('eligibilityStep'),
        on: {
          SET_ELID: [
            {
              actions: ['assignElidData', 'assignIsMinorData'],
              target: '#esiFlow',
              cond: 'isExpressScripts',
            },
            {
              actions: ['assignElidData', 'assignIsMinorData'],
              target: '#commonFlow',
              cond: 'isCompanyConfigLoaded',
            },
            {
              actions: ['assignElidData', 'assignIsMinorData'],
              target: '#autoEligibilityFlow',
            },
          ],
        },
      },

      noStateEligibility: {
        entry: () => trackPageView('noStateEligibility'),
        tags: ['noStateEligibility', 'ui'],
        id: 'noStateEligibility',
        type: 'final',
      },

      internalError: {
        entry: () => trackPageView('internalError'),
        tags: ['internalError', 'ui'],
        id: 'internalError',
      },

      autoEligibilityFlow: {
        id: 'autoEligibilityFlow',
        description:
          'Steps for all companies without special eligibility requirements',
        initial: 'performEligibilityCheck',
        states: {
          performEligibilityCheck: {
            tags: 'loading',
            invoke: {
              id: 'checkCompanyEligibility',
              src: 'checkCompanyEligibility',
            },
            on: {
              COMPANY_ID_FOUND: 'fetchCompanyConfig',
              COMPANY_ID_NOT_FOUND: 'companySelector',
            },
          },
          fetchCompanyConfig: {
            tags: 'loading',
            invoke: {
              id: 'fetchCompanyConfig',
              src: 'fetchCompanyConfig',
            },
            on: {
              COMPANY_CONFIG_FOUND: {
                actions: ['assignCompanyConfig', 'trackPolyaddicitonEnabled'],
                target: 'calculateFlowVersion',
              },
            },
          },
          companySelector: {
            entry: () => trackPageView('companySelector'),
            tags: ['companySelector', 'ui'],
            on: {
              COMPANY_ID_FOUND: 'fetchCompanyConfig',
            },
          },
          calculateFlowVersion: {
            invoke: {
              id: 'calculateFlowVersion',
              src: 'calculateFlowVersion',
            },
            on: {
              SET_FLOW_VERSION: [
                {
                  cond: 'isExpressScripts',
                  target: '#esiFlow',
                },
                {
                  ...LIGHTER_BASED_SET_FLOW_VERSION_CONFIG,
                  actions: [
                    ...(LIGHTER_BASED_SET_FLOW_VERSION_CONFIG.actions || []),
                    'revertMemberAddressIfNotUS',
                  ],
                },
                {
                  target: '#commonFlow',
                },
              ],
            },
          },
        },
      },
      commonFlow: {
        id: 'commonFlow',
        initial: 'fetchStateRestrictions',
        states: {
          fetchStateRestrictions: {
            tags: 'loading',
            invoke: {
              id: 'fetchStateRestrictions',
              src: 'checkStateAvailability',
            },
            on: {
              UPDATE_TREATMENT_PROGRAMS: {
                actions: 'updateAvailableTreatmentPrograms',
                target: 'checkIfTreatmentProgramsAvailable',
              },
            },
          },
          checkIfTreatmentProgramsAvailable: {
            always: [
              {
                target: 'addictionSelection',
                cond: 'areTreatmentProgramsAvailable',
              },
              {
                target: '#noStateEligibility',
              },
            ],
          },
          addictionSelection: {
            initial: 'performAutoAddictionSelectionCheck',
            onDone: 'signUpStep2',
            states: {
              performAutoAddictionSelectionCheck: {
                tags: 'loading',
                invoke: {
                  src: 'performAutoAddictionSelectionCheck',
                },
                on: {
                  SUBMIT_ADDICTION_AUTO_SELECTION: {
                    actions: [
                      'setIsAutomaticAddictionSelection',
                      'trackAddictionSelectorPageView',
                      'trackSubstanceSelectionPageView',
                    ],
                    target: 'submitAddictionSelection',
                  },
                  ADDICTION_NOT_AUTO_SELECTION: {
                    target: 'addictionSelector',
                  },
                },
              },
              addictionSelector: {
                entry: () => [
                  'trackAddictionSelectorPageView',
                  'trackSubstanceSelectionPageView',
                ],
                tags: ['addictionSelector', 'ui'],
                id: 'addictionSelector',
                on: {
                  SUBMIT_ADDICTION_SELECTION: {
                    target: 'submitAddictionSelection',
                  },
                },
              },
              submitAddictionSelection: {
                invoke: {
                  src: 'submitAddictionSelection',
                },
                on: {
                  SET_ACCESS_CODE: {
                    actions: 'assignAccessCode',
                  },
                  SET_QUITTING_PRIORITY: {
                    actions: 'setQuittingPriority',
                  },
                  SET_ADDICTION_TYPE: {
                    actions: ['setSelectedAddiction', 'trackAddictionSelect'],
                    target: 'updateAddictionsBasedOnAgeIfNeeded',
                  },
                },
              },
              updateAddictionsBasedOnAgeIfNeeded: {
                tags: 'loading',
                always: [
                  {
                    cond: 'shouldUpdateAddictionsBasedOnAge',
                    actions: 'updateQuittingBasedOnAge',
                    target: 'done',
                  },
                  {
                    target: 'done',
                  },
                ],
              },
              done: {
                type: 'final',
              },
            },
          },

          signUpStep2: {
            entry: () => trackPageView('signUpStep2'),
            tags: ['signUpStep2', 'ui'],
            initial: 'idle',
            id: 'signUpStep2',
            states: {
              idle: {
                on: {
                  EMAIL_FILLED_IN: {
                    actions: 'trackEmailFilledIn',
                  },
                  SET_SIGNUP_DATA: {
                    actions: ['assignAdditionalUserData'],
                    target: 'loading',
                  },
                },
              },
              loading: {
                tags: 'loading',
                invoke: {
                  id: 'createNewUserAccount',
                  src: 'createNewUserAccount',
                  onDone: [
                    {
                      target: '#motivationalQuestion.downloadTheApp',
                      cond: 'shouldSkipOnboardingCall',
                      actions: 'trackRegistrationComplete',
                    },
                    {
                      target: 'minorBeInTouchCheck',
                      cond: 'beInTouchCheck',
                    },
                    {
                      target: '#motivationalQuestion.preBookCallOrBeInTouch',
                      actions: [
                        'assignStarterCallSchedulerData',
                        'trackRegistrationComplete',
                      ],
                    },
                  ],
                  onError: [
                    {
                      target: '#internalError',
                      cond: 'internalErrorCheck',
                    },
                    { actions: ['assignErrors'], target: 'idle' },
                  ],
                },
              },
              minorBeInTouchCheck: {
                tags: 'loading',
                always: [
                  {
                    cond: (context) => context.user.isMinor,
                    target: '#motivationalQuestion.flowDone',
                  },
                  { target: '#motivationalQuestion.preBookCallOrBeInTouch' },
                ],
              },
            },
          },
          downloadTheApp: {
            entry: [
              (context) =>
                trackPageView(
                  context.flowVersion === FlowVersion.LighterV1
                    ? 'lighterDownloadApp'
                    : 'downloadTheApp'
                ),
              'sendWebkitCallback',
              () => closeWindowIfWebView(),
            ],
            id: 'downloadTheApp',
            tags: ['downloadTheApp', 'ui'],
            type: 'final',
          },
        },
      },

      lighterFlow: {
        id: 'lighterFlow',
        initial: 'performAutoAddictionSelectionCheck',
        exit: 'unsetShouldSaveUserProgress', // we shouldn't save user progress outside of lighter flow
        states: {
          performAutoAddictionSelectionCheck: {
            tags: 'loading',
            invoke: {
              src: 'performAutoAddictionSelectionCheck',
            },
            on: {
              SUBMIT_ADDICTION_AUTO_SELECTION: {
                actions: [
                  'setIsAutomaticAddictionSelection',
                  'trackSubstanceSelectionPageView',
                ],
                target: 'submitAddictionSelection',
              },
              ADDICTION_NOT_AUTO_SELECTION: {
                target: 'lighterAddictionSelector',
              },
            },
          },
          lighterAddictionSelector: {
            tags: ['lighterAddictionSelector', 'ui'],
            entry: 'trackSubstanceSelectionPageView',
            id: 'lighterAddictionSelector',
            on: {
              SUBMIT_ADDICTION_SELECTION: {
                target: 'submitAddictionSelection',
              },
            },
          },
          submitAddictionSelection: {
            invoke: {
              src: 'submitAddictionSelection',
            },
            on: {
              SET_QUITTING_PRIORITY: {
                actions: 'setQuittingPriority',
              },
              SET_ADDICTION_TYPE: {
                actions: ['setSelectedAddiction', 'trackAddictionSelect'],
                target: 'lighterEligibilityName',
              },
            },
          },
          lighterEligibilityName: {
            tags: ['lighterEligibilityName', 'ui'],
            id: 'lighterEligibilityName',
            entry: () => trackPageView('lighterEligibilityName'),
            on: {
              UPDATE_LIGHTER_MEMBER_DATA: {
                actions: updateLighterMemberData,
                target: 'lighterAccountCreation',
              },
            },
          },
          lighterAccountCreation: {
            tags: ['lighterAccountCreation', 'ui'],
            id: 'lighterAccountCreation',
            initial: 'idle',
            entry: () => trackPageView('lighterAccountCreation'),
            states: {
              idle: {
                on: {
                  UPDATE_LIGHTER_MEMBER_DATA: {
                    actions: [
                      'clearAccountCreationErrors',
                      updateLighterMemberData,
                    ],
                    target: 'loading',
                  },
                },
              },
              loading: {
                tags: 'loading',
                invoke: {
                  id: 'createLighterAccount',
                  src: 'createLighterAccount',
                  onDone: {
                    actions: 'setShouldSaveUserProgress', // we should start saving user progress only after creating cognito account
                    target: '#lighterPostAccountCreation',
                  },
                  onError: [
                    {
                      target: '#internalError',
                      cond: 'internalErrorCheck',
                    },
                    {
                      target: '#lighterAddictionSelector',
                      cond: 'noSelectedAddictionErrorCheck',
                    },
                    { actions: ['assignErrors'], target: 'idle' },
                  ],
                },
              },
            },
          },
          lighterPostAccountCreation: {
            id: 'lighterPostAccountCreation',
            initial: 'checkFlow',
            entry: 'setShouldSaveUserProgress',
            states: {
              checkFlow: {
                always: [
                  {
                    cond: 'isHealthPlanFlowContext',
                    target: '#healthPlanFlow',
                  },
                  {
                    target: 'normalLighterPostAccountCreationStep',
                  },
                ],
              },
              normalLighterPostAccountCreationStep: {
                always: { target: '#lighterEligibilityAddress' },
              },
            },
          },
          lighterEligibilityAddress: {
            tags: ['lighterEligibilityAddress', 'ui'],
            id: 'lighterEligibilityAddress',
            entry: () => trackPageView('lighterEligibilityAddress'),
            initial: 'idle',
            states: {
              idle: {
                on: {
                  UPDATE_LIGHTER_MEMBER_DATA: {
                    actions: updateLighterMemberData,
                    target: 'fetchStateRestrictions',
                  },
                },
              },
              fetchStateRestrictions: {
                tags: 'loading',
                invoke: {
                  id: 'fetchStateRestrictions',
                  src: 'checkStateAvailability',
                },
                on: {
                  UPDATE_TREATMENT_PROGRAMS: [
                    {
                      actions: 'updateAvailableTreatmentPrograms',
                      cond: 'lighterAreMultipleAddctionsSelected',
                      target: 'checkIfTreatmentProgramsAvailable',
                    },
                    {
                      actions: 'updateAvailableTreatmentPrograms',
                      target: 'checkIfSingularTreatmentProgramAvailable',
                    },
                  ],
                },
              },
              checkIfTreatmentProgramsAvailable: {
                always: [
                  {
                    target: '#lighterEligibilityBirth',
                    cond: 'lighterAllSelectedAddictionsAvailable',
                    actions: [() => trackStateAvailability()],
                  },
                  {
                    target: 'updateUserDetails',
                    actions: ['updateQuittingBasedOnStateAvailability'],
                  },
                ],
              },
              checkIfSingularTreatmentProgramAvailable: {
                always: [
                  {
                    target: '#lighterEligibilityBirth',
                    cond: 'lighterIsSelectedAddictionAvailable',
                    actions: [() => trackStateAvailability()],
                  },
                  {
                    cond: () => isUnavailableStateEnabledFeatureSwitch,
                    actions: [
                      'setStateUnavailable',
                      'updateQuittingBasedOnStateAvailability',
                    ],
                    target: 'updateUserDetails',
                  },
                  {
                    actions: ['setStateUnavailable'],
                    target: '#lighterStateUnavailable',
                  },
                ],
              },
              updateUserDetails: {
                tags: 'loading',
                entry: (context) =>
                  trackStateAvailability(context.unavailability.state),
                invoke: {
                  id: 'updateUserDetails',
                  src: 'updateUserDetails',
                  onDone: [
                    {
                      cond: (context) =>
                        !!context.unavailability.state?.partial,
                      target: '#lighterEligibilityBirth',
                    },
                    {
                      cond: () => isUnavailableStateEnabledFeatureSwitch,
                      target: '#lighterEligibilityBirth',
                    },
                    {
                      target: '#lighterStateUnavailable',
                    },
                  ],
                  onError: isUnavailableStateEnabledFeatureSwitch
                    ? '#lighterEligibilityBirth'
                    : '#internalError',
                },
              },
            },
          },
          lighterEligibilityBirth: {
            tags: ['lighterEligibilityBirth', 'ui'],
            id: 'lighterEligibilityBirth',
            entry: () => [
              trackPageView('lighterEligibilityBirth'),
              clearLoginUserData,
            ],
            initial: 'idle',
            onDone: '#lighterPhoneNumber',
            states: {
              idle: {
                id: 'lighterEligibilityBirthIdle',
                on: {
                  UPDATE_LIGHTER_MEMBER_DATA: {
                    actions: [
                      'clearMinimumAgeError',
                      updateLighterMemberData,
                      'assignIsMinorData',
                    ],
                    target: 'updateAddictionsBasedOnAgeIfNeeded',
                  },
                },
              },
              updateAddictionsBasedOnAgeIfNeeded: {
                tags: 'loading',
                always: [
                  {
                    cond: 'shouldUpdateAddictionsBasedOnAge',
                    actions: 'updateQuittingBasedOnAge',
                    target: 'updateUserDetails',
                  },
                  {
                    target: 'done',
                  },
                ],
              },
              updateUserDetails: {
                tags: 'loading',
                invoke: {
                  id: 'updateUserDetails',
                  src: 'updateUserDetails',
                  onDone: 'done',
                  onError: 'done',
                },
              },
              done: {
                type: 'final',
              },
            },
          },
          lighterPhoneNumber: {
            tags: ['lighterPhoneNumber', 'ui'],
            id: 'lighterPhoneNumber',
            entry: () => trackPageView('lighterPhoneNumber'),
            initial: 'idle',
            states: {
              idle: {
                on: {
                  UPDATE_LIGHTER_MEMBER_DATA: {
                    actions: updateLighterMemberData,
                    target: 'loading',
                  },
                },
              },
              loading: {
                tags: 'loading',
                invoke: {
                  id: 'lighterB2BSave',
                  src: 'lighterB2BSave',
                  onDone: [
                    {
                      actions: 'unsetShouldSaveUserProgress', // we don't need to save user progress after saving b2b details
                      target: 'minorBeInTouchCheck',
                      cond: 'beInTouchCheck',
                    },
                    {
                      actions: [
                        'unsetShouldSaveUserProgress', // we don't need to save user progress after saving b2b details
                        'assignStarterCallSchedulerData',
                      ],
                      target: '#motivationalQuestion.preBookCallOrBeInTouch',
                    },
                  ],
                  onError: [
                    {
                      target: 'minorBeInTouchCheck',
                      cond: 'internalErrorCheck',
                    },
                    { actions: ['assignErrors'], target: 'idle' },
                  ],
                },
              },
              minorBeInTouchCheck: {
                tags: 'loading',
                always: [
                  {
                    cond: (context) => context.user.isMinor,
                    target: '#motivationalQuestion.flowDone',
                  },
                  { target: '#motivationalQuestion.preBookCallOrBeInTouch' },
                ],
              },
            },
          },
          motivationalQuestion: {
            tags: ['motivationalQuestion', 'ui'],
            id: 'motivationalQuestion',
            entry: () =>
              trackPageView('motivationalQuestion', {
                incentiveQuestion: !!getIncentive(),
              }),
            states: {
              preBookCallOrBeInTouch: {
                initial: 'idle',
                states: {
                  idle: {
                    on: {
                      UPDATE_MOTIVATION_DATA: {
                        actions: updateMotivationData,
                        target: 'loading',
                      },
                    },
                  },
                  loading: {
                    tags: 'loading',
                    invoke: {
                      id: 'saveMotivations',
                      src: 'saveMotivations',
                      onDone: '#preBookCallOrBeInTouch',
                      onError: '#preBookCallOrBeInTouch',
                    },
                  },
                },
              },
              downloadTheApp: {
                initial: 'idle',
                states: {
                  idle: {
                    on: {
                      UPDATE_MOTIVATION_DATA: {
                        actions: updateMotivationData,
                        target: 'loading',
                      },
                    },
                  },
                  loading: {
                    tags: 'loading',
                    invoke: {
                      id: 'saveMotivations',
                      src: 'saveMotivations',
                      onDone: '#downloadTheApp',
                      onError: '#downloadTheApp',
                    },
                  },
                },
              },
              flowDone: {
                initial: 'idle',
                states: {
                  idle: {
                    on: {
                      UPDATE_MOTIVATION_DATA: {
                        actions: updateMotivationData,
                        target: 'loading',
                      },
                    },
                  },
                  loading: {
                    tags: 'loading',
                    invoke: {
                      id: 'saveMotivations',
                      src: 'saveMotivations',
                      onDone: '#flowDone',
                      onError: '#flowDone',
                    },
                  },
                },
              },
            },
          },
          preBookCallOrBeInTouch: {
            id: 'preBookCallOrBeInTouch',
            initial: 'idle',
            states: {
              idle: {
                always: [
                  {
                    cond: 'isEligibleForCallChoice',
                    target: 'callChoiceDecision',
                  },
                  {
                    target: 'done',
                    actions: 'trackOnboardingSelection',
                  },
                ],
              },
              callChoiceDecision: {
                tags: ['callChoiceDecision', 'ui'],
                id: 'callChoiceDecision',
                entry: () => trackPageView('callChoiceDecision'),
                initial: 'idle',
                states: {
                  idle: {
                    on: {
                      UPDATE_LIGHTER_MEMBER_DATA: {
                        actions: [
                          updateLighterMemberData,
                          'trackOnboardingSelection',
                        ],
                        target: 'loading',
                      },
                    },
                  },
                  loading: {
                    tags: 'loading',
                    invoke: {
                      id: 'saveB2BDetails',
                      src: 'saveB2BDetails',
                      onDone: [
                        {
                          cond: (context) =>
                            context.user.onboardingType ===
                            OnboardingType.DigitalOnboarding,
                          target: '#afterBookCallOrBeInTouch',
                        },
                        { target: 'done' },
                      ],
                      onError: 'done',
                    },
                  },
                  done: { type: 'final' },
                },
                onDone: 'done',
              },
              done: { type: 'final' },
            },
            onDone: [
              {
                cond: (context) =>
                  !!(
                    context.starterCallSchedulerData.healthieUrl ||
                    context.starterCallSchedulerData.nylasUrl
                  ),
                target: 'bookCall',
              },
              { target: 'beInTouch' },
            ],
          },
          beInTouch: {
            entry: (context) =>
              trackPageView(
                context.flowVersion === FlowVersion.LighterV1
                  ? 'lighterWellBeInTouch'
                  : 'beInTouch'
              ),
            tags: ['beInTouch', 'ui'],
            id: 'beInTouch',
            on: {
              NEXT_STEP: '#afterBookCallOrBeInTouch',
            },
          },
          lighterStateUnavailable: {
            tags: ['lighterStateUnavailable', 'ui'],
            id: 'lighterStateUnavailable',
            entry: () => trackPageView('lighterStateUnavailable'),
            initial: 'idle',
            states: {
              idle: {
                on: {
                  CALL_BOOKED: {
                    target: 'done',
                  },
                },
              },
              done: {
                type: 'final',
              },
            },
            onDone: {
              actions: [signOut, clearUserData],
            },
          },
          bookCall: {
            tags: ['bookCall', 'ui'],
            id: 'bookCall',
            initial: 'idle',
            entry: (context) => {
              /**
               *  CallCopyVariation and getCallCopyVariation used in the BookCall page to
               *  determine copy. What is used here should reflected what is used there.
               */
              const pageName =
                context.flowVersion === FlowVersion.LighterV1
                  ? 'lighterBookCall'
                  : 'starterCall'
              trackPageView(pageName, {
                copyVariation: getCallCopyVariation(
                  context.user.isMinor,
                  context.unavailability.state
                ),
                incentiveReminder: !!getIncentive(),
              })
            },
            states: {
              idle: {
                on: {
                  CALL_BOOKED: {
                    target: '#afterBookCallOrBeInTouch',
                  },
                },
              },
            },
          },
          afterBookCallOrBeInTouch: {
            id: 'afterBookCallOrBeInTouch',
            always: 'flowDone',
          },
          flowDone: {
            id: 'flowDone',
            entry: [signOut],
            always: [
              {
                cond: (context) => context.user.isMinor,
                target: 'successfullyRegistered',
                actions: [clearUserData],
              },
              {
                target: '#downloadTheApp',
                actions: [clearUserData],
              },
            ],
          },
          successfullyRegistered: {
            id: 'successfullyRegistered',
            tags: ['successfullyRegistered', 'ui'],
            entry: [
              (context) => {
                const pageName =
                  context.flowVersion === FlowVersion.LighterV1
                    ? 'lighterSuccessfullyRegistered'
                    : 'successfullyRegistered'
                trackPageView(pageName)
              },
            ],
            type: 'final',
          },
        },
      },
    },
  },
  {
    actions: {
      assignContext: pure<
        SignupContext,
        DoneInvokeEvent<{
          message?: SignupMachineError
          context?: ProgressSignupContext
        }>
      >((context, event) => {
        const assignActions: AssignAction<
          SignupContext,
          DoneInvokeEvent<{
            message?: SignupMachineError
            context?: ProgressSignupContext
          }>
        >[] = [
          assign<SignupContext>(() => {
            return merge(
              {},
              context,
              event.data.context || { shouldSaveUserProgress: false }
            )
          }),
        ]

        if (event.data.context?.flowVersion) {
          assignActions.push(
            setFlowVersion(
              event.data.context?.flowVersion,
              'Flow version set from progress'
            )
          )
        }

        return assignActions
      }),
      // Converts a `Company` into a type for storage in the current state
      // machine context.
      assignCompanyConfig: assign({
        company: (context, event) => ({
          carrierId: context.company.carrierId,
          subCompanyId: context.company.subCompanyId,
          companyId: event.value.id,
          country: event.value.country,
          dataRegion: event.value.dataRegion,
          gbEthnicity: event.value.gbEthnicity,
          multipleLanguages: event.value.spanishEnabled,
          name: event.value.name,
          requireSouthAfricaIDNumber: event.value.requireSouthAfricaIDNumber,
          slug: event.value.slug,
          polyaddictionEnabled: event.value.polyaddictionEnabled,
          minorsProgramEnabled: event.value.minorsProgramEnabled,
          callChoiceEnabled: event.value.callChoiceEnabled,
          flowVersionsEnabled: event.value.flowVersionsEnabled,
        }),
        // We use `enabledTreatmentPrograms` as this is the property name used
        // in the company service
        availableTreatmentPrograms: (_, event) =>
          event.value.enabledTreatmentPrograms,
      }),
      assignResetPasswordBackendError: assign({
        resetPassword: (context, event) => ({
          ...context.resetPassword,
          backendErrors: {
            codeMismatch:
              event.data.message ===
              SignupMachineError.ResetPasswordCodeMismatch,
            invalidPassword:
              event.data.message ===
              SignupMachineError.ResetPasswordInvalidPassword,
            sthWentWrong:
              event.data.message ===
              SignupMachineError.ResetPasswordSthWentWrong,
          },
        }),
      }),
      clearResetPasswordBackendErrors: assign({
        resetPassword: (context) => ({
          ...context.resetPassword,
          backendErrors: {
            codeMismatch: false,
            invalidPassword: false,
            sthWentWrong: false,
          },
        }),
      }),
      clearResetPasswordContext: assign({
        resetPassword: (context) => ({
          ...context.resetPassword, // needed only because of typing issue
          cognitoClientId: '',
          cognitoUserPoolId: '',
          email: '',
          backendErrors: {
            codeMismatch: false,
            invalidPassword: false,
            sthWentWrong: false,
          },
        }),
      }),
      updateResetPasswordContext: assign({
        resetPassword: (context, event) => ({
          cognitoClientId: event.data.cognitoClientId,
          cognitoUserPoolId: event.data.cognitoUserPoolId,
          email: event.data.email,
          backendErrors: {
            codeMismatch: false,
            invalidPassword: false,
            sthWentWrong: false,
          },
        }),
      }),
      assignIsMinorData: assign({
        user: (context) => {
          const isMinor =
            context.user.address.country === Country.US &&
            !!isMinorUS(context.user.dateOfBirth, context.user.address.province)
          trackMinorRegistrationStatus(isMinor)
          return merge({}, context.user, { isMinor })
        },
      }),
      trackPolyaddicitonEnabled: (_, event) =>
        trackPolyaddictionEnabled(event.value?.polyaddictionEnabled || false),
      trackOnboardingSelection: (context) =>
        trackOnboardingSelection(context.user.onboardingType),
      assignGBUser: assign({
        user: (context) =>
          merge({}, context.user, {
            address: {
              country: Country.GB,
            },
          }),
      }),
      assignZAUser: assign({
        user: (context) =>
          merge({}, context.user, {
            address: {
              country: Country.ZA,
            },
          }),
      }),
      assignAccessCode: assign({
        user: (context, event) =>
          merge({}, context.user, {
            accessCode: event.value.accessCode,
          }),
      }),
      setFlowVersion: pure((_, event: SetFlowVersionEvent) => {
        return [
          setFlowVersion(event.value.flowVersion, event.value.log),
        ] as AssignAction<SignupContext, SetFlowVersionEvent>[]
      }),
      setSelectedAddiction: assign({
        selectedAddiction: (_, event) => event.value,
      }),
      setQuittingPriority: assign({
        quittingPriority: (_, event) => event.value,
      }),
      setPolyAddictions: assign({
        polyAddictions: (_, event) => event.value,
      }),
      updateQuittingBasedOnAge: pure((context) => {
        const age = calculateUserAge(context.user.dateOfBirth)

        const {
          newSelectedAddiction,
          newQuittingPriority,
          availablePrograms,
          unavailablePrograms,
        } = getUpdatedSelectedAddictionAndPriorities(
          context.quittingPriority || {},
          (quitting) => doesMeetAgeThresholdForProgramme(age, quitting),
          { ageAtEnrolment: age }
        )

        return [
          assign({
            selectedAddiction: newSelectedAddiction,
          }),
          assign({
            quittingPriority: newQuittingPriority,
          }),
          assign({
            unavailability: merge({}, context.unavailability, {
              age: {
                unavailablePrograms: unavailablePrograms,
                availablePrograms: availablePrograms,
                partial:
                  !!availablePrograms.length && !!unavailablePrograms.length,
              },
            }),
          }),
        ] as unknown as AssignAction<SignupContext, any>[]
      }),
      setStateUnavailable: pure((context) => {
        const stateUnavailability = {
          unavailablePrograms: [context.selectedAddiction as TreatmentProgram],
          availablePrograms: [],
          partial: false,
        }
        trackStateAvailability(stateUnavailability)
        return assign({
          unavailability: (context) =>
            merge({}, context.unavailability, {
              state: stateUnavailability,
            }),
        }) as AssignAction<SignupContext, any>
      }),
      // Need to check if in the future we plan to launch with learn.
      updateQuittingBasedOnStateAvailability: pure((context) => {
        const {
          newSelectedAddiction,
          newQuittingPriority,
          availablePrograms,
          unavailablePrograms,
        } = getUpdatedSelectedAddictionAndPriorities(
          context.quittingPriority || {},
          (quitting) => context.availableTreatmentPrograms[quitting],
          { state: context.user.address.province }
        )

        return [
          assign({
            selectedAddiction: newSelectedAddiction,
          }),
          assign({
            quittingPriority: newQuittingPriority,
          }),
          assign({
            unavailability: merge({}, context.unavailability, {
              state: {
                unavailablePrograms: unavailablePrograms,
                availablePrograms: availablePrograms,
                partial:
                  !!availablePrograms.length && !!unavailablePrograms.length,
              },
            }),
          }),
        ] as unknown as AssignAction<SignupContext, any>[]
      }),
      trackAddictionSelect: (_, event) =>
        trackAddiction(event.value as TreatmentProgram),
      trackPolyAddictions: (_, event) => trackPolyAddictions(event.value),
      setNoMembersFoundError: assign({
        errors: (context) =>
          merge({}, context.errors, {
            accessCode: {
              noMatchingEsiMembers: true,
            },
          }),
      }),
      assignElidData: assign({
        user: (context, event) => {
          const {
            country,
            dateOfBirth,
            firstName,
            lastName,
            postalCode,
            province,
            zaIdNumber,
          } = event.value

          return merge({}, context.user, {
            address: {
              country,
              postalCode,
              province,
            },
            cellPhoneCountryCode: '+' + getCountryCallingCode(country),
            dateOfBirth,
            firstName,
            lastName,
            ...(zaIdNumber && { zaIdNumber }),
          })
        },
      }),
      revertMemberAddressIfNotUS: assign({
        user: (context) =>
          merge(
            {},
            context.user,
            context.user.address.country === Country.US
              ? {}
              : {
                  address: { ...INITIAL_USER.address },
                  cellPhoneCountryCode: INITIAL_USER.cellPhoneCountryCode,
                }
          ),
      }),
      updateAvailableTreatmentPrograms: assign({
        // This is a pessimistic update, i.e. `true + false = false`. This is
        // because we're modelling a series of restrictions which all have to
        // pass in order to offer a treatment program.
        //
        // e.g. if a company offers opioids, but we aren't licenced in a
        // member's state, we cannot offer the opioids program.
        availableTreatmentPrograms: (context, event) =>
          mergeWith(
            {},
            context.availableTreatmentPrograms,
            event.value,
            (objValue, srcValue, key, object) =>
              object[key] === undefined ? srcValue : srcValue && objValue
          ),
      }),
      updateTreatmentAccessCodes: assign({
        treatmentAccessCode: (context, event) =>
          merge({}, context.treatmentAccessCode, event.value),
      }),
      assignStarterCallSchedulerData: assign({
        starterCallSchedulerData: (context, event) =>
          merge({}, context.starterCallSchedulerData, event.data),
      }),
      assignAdditionalUserData: assign({
        user: (context, event) =>
          merge({}, context.user, {
            address: {
              address1: event.value.address1,
              address2: event.value.address2,
              city: event.value.city,
            },
            cellPhoneNumber: event.value.cellPhoneNumber,
            communicationsOptIn: event.value.communicationsOptIn,
            email: event.value.email,
            ethnicity: event.value.ethnicity,
            genderIdentity: event.value.genderIdentity,
            genderPronouns: event.value.genderPronouns,
            language: event.value.language,
            password: event.value.password,
            sex: event.value.sex,
          }),
      }),
      assignCarrierId: assign({
        company: (context, event) =>
          merge({}, context.company, {
            carrierId: event.value.carrierId,
          }),
      }),
      assignSubCompanyId: assign({
        company: (context, event) =>
          merge({}, context.company, {
            subCompanyId: event.value.subCompanyId,
          }),
      }),
      clearAccountCreationErrors: assign({
        errors: (context) =>
          merge({}, context.errors, {
            signUp: {
              emailInUse: false,
              passwordLength: false,
            },
          }),
      }),
      assignErrors: assign({
        errors: (context, event) =>
          merge({}, context.errors, {
            accessCode: {
              noMatchingEsiMembers: event.data.noMatchingEsiMembers,
            },
            signIn: {
              loginFailed: event.data.loginFailed,
            },
            signUp: {
              emailInUse: event.data.emailInUse,
              passwordLength: event.data.passwordLength,
            },
          }),
      }),
      clearLoginFailedError: assign({
        errors: (context) =>
          merge({}, context.errors, {
            signIn: {
              loginFailed: false,
            },
          }),
      }),
      clearMinimumAgeError: assign({
        errors: (context) =>
          merge({}, context.errors, {
            dateOfBirth: {
              isNotMinimumAge: false,
            },
          }),
      }),
      setIsAutomaticAddictionSelection: assign<SignupContext>({
        isAutomaticAddictionSelection: () => true,
      }),
      setShouldSaveUserProgress: assign({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        shouldSaveUserProgress: (_context) => true,
      }),
      unsetShouldSaveUserProgress: assign({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        shouldSaveUserProgress: (_context) => false,
      }),
      trackEmailFilledIn: (_, event) => {
        trackEmailFilledIn(event.value.email)
      },
      trackSubstanceSelectionPageView: (context) => {
        trackPageView('substanceSelectionPage', {
          autoSelection: context.isAutomaticAddictionSelection,
        })
      },
      trackAddictionSelectorPageView: (context) => {
        trackPageView('addictionSelector', {
          autoSelection: context.isAutomaticAddictionSelection,
        })
      },
      sendWebkitCallback: (context) => {
        if (window.webkit?.messageHandlers?.signUpMessageHandler) {
          window.webkit.messageHandlers.signUpMessageHandler.postMessage({
            email: context.user.email,
          })
        }
      },
      trackRegistrationComplete: () => {
        trackRegistrationComplete()
      },
    },
    services: {
      performEsiEligibilityCheck: (context) => async (callback) => {
        try {
          const {
            company: { carrierId, subCompanyId },
            user: { dateOfBirth, lastName, address, accessCode },
          } = context
          const response = await fetchEsiEligibility({
            accessCode: accessCode.trim(),
            carrierId: carrierId.trim(),
            subCompanyId,
            dateOfBirth: dateOfBirth.trim(),
            lastName: lastName.trim(),
            zipCode: address.postalCode.trim(),
          })

          callback({
            type: 'UPDATE_TREATMENT_PROGRAMS',
            value: response.treatmentPrograms,
          })

          callback({
            type: 'UPDATE_TREATMENT_ACCESS_CODES',
            value: response.treatmentAccessCode,
          })

          callback('FOUND_ESI_MATCH')
        } catch (error: unknown) {
          if (error instanceof AxiosError && error.response) {
            switch (error.response.data.nextStep) {
              case 'NeedsAccessCode':
              case 'EnterAccessCode':
                callback('REQUEST_ACCESS_CODE')
                break
              case 'TryAgain':
                callback('NO_MATCHING_MEMBERS')
                break
              case 'NeedsCarrierId':
              case 'SelectEmployer':
                callback('REQUEST_CARRIER_ID')
                break
              default:
                callback('ERROR')
            }
          } else {
            callback('ERROR')
          }
        }
      },
      /**
       * checks if company is alcohol only & if alcohol addiction is available
       * @param context
       * @returns
       */
      performAutoAddictionSelectionCheck: (context) => async (callback) => {
        const company = companyService.getById(context.company.companyId)
        if (
          !!company &&
          onlyAlcoholProgramEnabled(company.enabledTreatmentPrograms) &&
          onlyAlcoholProgramEnabled(context.availableTreatmentPrograms)
        ) {
          callback({
            type: 'SUBMIT_ADDICTION_AUTO_SELECTION',
            value: {
              [AddictionSelectionFormName.SingleAddiction]:
                AddictionSelectionParentAddiction.Alcohol,
              [AddictionSelectionFormName.NicotineSubAddiction]: undefined,
            },
          })
        } else {
          callback('ADDICTION_NOT_AUTO_SELECTION')
        }
      },
      checkCompanyEligibility: (context) => async (callback) => {
        try {
          const { company, user } = context
          const response = await fetchCompanyEligibility({
            dateOfBirth: user.dateOfBirth,
            firstName: user.firstName,
            lastName: user.lastName,
            postalCode: user.address.postalCode,
            ...(company.companyId && { companyId: company.companyId }),
            ...(user.address.province && { province: user.address.province }),
          })
          if (response.data.success) {
            callback({
              type: 'COMPANY_ID_FOUND',
              value: response.data.companyId,
            })
          } else {
            throw new Error('companyIdNotFound')
          }
        } catch (error: unknown) {
          callback('COMPANY_ID_NOT_FOUND')
        }
      },
      checkStateAvailability: (context) => async (callback) => {
        // If the member is not in the US, we don't add any extra restrictions
        if (context.user.address.country !== Country.US) {
          return callback({
            type: 'UPDATE_TREATMENT_PROGRAMS',
            value: {
              alcohol: true,
              chewingTobacco: true,
              cigarettes: true,
              cigarettesLearn: true,
              eCigarettes: true,
              eCigarettesLearn: true,
              opioids: true,
            },
          })
        }

        // If the member is in the US, we need to see what we can provide in
        // their state
        try {
          const response = await fetchTreatmentProgramsForState(
            context.user.address.province
          )
          callback({
            type: 'UPDATE_TREATMENT_PROGRAMS',
            value: response,
          })
        } catch (error: unknown) {
          callback('ERROR')
        }
      },
      confirmNewPassword,
      createNewUserAccount,
      createLighterAccount,
      lighterB2BSave,
      resendPasswordResetEmail,
      saveMotivations,
      saveB2BDetails,
      sendPasswordResetEmail,
      signIn,
      updateUserDetails,
      getProgress,
      checkUrlForDocs: () => (callback) => {
        const urlParams = new URLSearchParams(window.location.search)

        const termsParamKey = 'terms_of_use'
        const hasTermsParam = urlParams.has(termsParamKey)
        const termsParam = urlParams.get(termsParamKey)

        const privacyParamKey = 'privacy_policy'
        const hasPrivacyParam = urlParams.has(privacyParamKey)
        const privacyParam = urlParams.get(privacyParamKey)

        const noticeOfPrivacyPracticesParamKey = 'notice_of_privacy_practices'
        const hasNoticeOfPrivacyPracticesParam = urlParams.has(
          noticeOfPrivacyPracticesParamKey
        )
        const noticeOfPrivacyPracticesParam = urlParams.get(
          noticeOfPrivacyPracticesParamKey
        )
        if (hasTermsParam && termsParam !== 'false') {
          callback({
            type: 'TERMS_OF_USE_FOUND',
          })
        } else if (hasPrivacyParam && privacyParam !== 'false') {
          callback({
            type: 'PRIVACY_POLICY_FOUND',
          })
        } else if (
          hasNoticeOfPrivacyPracticesParam &&
          noticeOfPrivacyPracticesParam !== 'false'
        ) {
          callback({
            type: 'NOTICE_OF_PRIVACY_PRACTICES_FOUND',
          })
        } else {
          callback('DOCS_NOT_FOUND')
        }
      },
      checkUrlForDownload: () => (callback) => {
        const downloadParamKey = 'download'
        const urlParams = new URLSearchParams(window.location.search)
        const hasDownloadParam = urlParams.has(downloadParamKey)
        const downloadParam = urlParams.get(downloadParamKey)
        if (hasDownloadParam && downloadParam !== 'false') {
          callback({
            type: 'DOWNLOAD_FOUND',
            value: { medium: urlParams.get('medium') },
          })
        } else {
          callback('DOWNLOAD_NOT_FOUND')
        }
      },
      checkUrlForLogin: () => (callback) => {
        const loginParamKey = 'login'
        const urlParams = new URLSearchParams(window.location.search)
        const hasLoginParam = urlParams.has(loginParamKey)
        const loginParam = urlParams.get(loginParamKey)
        if (
          isRejoinRegFlowEnabledFeatureSwitch &&
          hasLoginParam &&
          loginParam !== 'false'
        ) {
          callback('LOGIN_FOUND')
        } else {
          callback('LOGIN_NOT_FOUND')
        }
      },
      checkUrlForCompanyId: () => (callback) => {
        const urlParams = new URLSearchParams(window.location.search)
        const companyIdParam = urlParams.get('client_id')
        const companyId = companyIdParam ? parseInt(companyIdParam, 10) : 0
        if (companyId) {
          callback({
            type: 'COMPANY_ID_FOUND',
            value: companyId,
          })
        } else {
          callback('COMPANY_ID_NOT_FOUND')
        }
      },
      // This should be revisited as more flow versions are added and logic
      // needs change.
      calculateFlowVersion: (context) => (callback) => {
        const urlParams = new URLSearchParams(window.location.search)
        const flowVersionParam = urlParams.get('flow')?.trim()
        const flowVersionLocalStorage = getFlowVersionFromLocalStorage()

        let log = 'Default flow version original used'
        // Default flow version is original
        let flowVersion = FlowVersion.Original

        // If no company config found then original
        if (!context.company.companyId) {
          flowVersion = FlowVersion.Original
          log = 'Flow version set to original as no company config found'
        }
        // If a flow version query param exists in the url, it matches a flow
        // version and that version is enabled according to the company config
        // then flow version is query param flow version
        else if (
          flowVersionParam &&
          FlowVersions.includes(flowVersionParam as FlowVersion) &&
          !!context.company.flowVersionsEnabled?.[
            flowVersionParam as FlowVersion
          ]
        ) {
          flowVersion = flowVersionParam as FlowVersion
          log = 'Flow version query param used'
          // If flow version stored (and not expired), check that the flow
          // version is enabled or is original flow.
        } else if (
          flowVersionLocalStorage &&
          FlowVersions.includes(flowVersionLocalStorage) &&
          context.company.flowVersionsEnabled &&
          context.company.flowVersionsEnabled[flowVersionLocalStorage]
        ) {
          flowVersion = flowVersionLocalStorage
          log = 'Flow version local storage item used'
        } else if (
          context.company.flowVersionsEnabled &&
          Object.values(context.company.flowVersionsEnabled).some(
            (isEnabled) => isEnabled
          )
        ) {
          const prioritisedFlowVersion = FlowVersionPriorityOrder.find(
            (flow) => context.company.flowVersionsEnabled![flow]
          )
          if (prioritisedFlowVersion) {
            flowVersion = prioritisedFlowVersion
            log = 'Flow version set (from company config)'
          }
        }

        callback({
          type: 'SET_FLOW_VERSION',
          value: { flowVersion, log },
        })
      },
      fetchCompanyConfig: (_, event) => (callback) => {
        try {
          const config = companyService.getById(event.value)

          if (config) {
            trackCompany(config.id, config.slug)
            callback({
              type: 'COMPANY_CONFIG_FOUND',
              value: config,
            })
          } else {
            throw new Error('companyConfigNotFound')
          }
        } catch (error: unknown) {
          callback('ERROR')
        }
      },
      submitAddictionSelection,
    },
    guards: {
      isEligibleForCallChoice: (context) =>
        isCallChoiceEnabledFeatureSwitch &&
        !!context.company.callChoiceEnabled &&
        (isAllPlaftormsForCallChoiceEnabledFeatureSwitch ||
          getOperatingSystem() === OperatingSystem.iOS) &&
        !!context.quittingPriority &&
        !!context.quittingPriority[1] &&
        !context.quittingPriority[1].state && // program is available in state
        !context.quittingPriority[1].ageAtEnrolment && // program is available for age
        !context.user.isMinor, // member is not minor
      isHealthPlanFlowContext: (context) =>
        context.flowVersion === FlowVersion.HealthPlan,
      lighterAreMultipleAddctionsSelected: (context) =>
        !!context.polyAddictions && context.polyAddictions?.length > 1,
      lighterIsSelectedAddictionAvailable: (context) =>
        !!context.selectedAddiction &&
        !!context.availableTreatmentPrograms[context.selectedAddiction],
      lighterAllSelectedAddictionsAvailable: (context) =>
        !!context.polyAddictions &&
        context.polyAddictions?.every(
          (addiction) =>
            !!context.availableTreatmentPrograms[
              addiction as keyof TreatmentProgramConfig
            ]
        ),
      shouldUpdateAddictionsBasedOnAge: (context: SignupContext) => {
        const age = calculateUserAge(context.user.dateOfBirth)
        return !(
          context.polyAddictions?.length
            ? context.polyAddictions
            : [context.selectedAddiction as TreatmentProgram]
        ).every((quitting) => doesMeetAgeThresholdForProgramme(age, quitting))
      },
      areTreatmentProgramsAvailable: (context) =>
        Object.values(context.availableTreatmentPrograms).some(
          (v) => v === true
        ),
      beInTouchCheck: (_, event) => event.data.message === 'beInTouch',
      isCompanyGB: (_, event) => event.value.country === Country.GB,
      isCompanyZA: (_, event) => event.value.country === Country.ZA,
      isCompanyConfigLoaded: (context) => !!context.company.name,
      isExpressScripts: (context) => context.company.companyId === 27,
      isResetPasswordExpiredCodeErrorCheck: (_, event) =>
        event.data.message === SignupMachineError.ResetPasswordExpiredCode,
      internalErrorCheck: (_, event) =>
        event.data.message === SignupMachineError.InternalError,
      noSelectedAddictionErrorCheck: (_, event) =>
        event.data.message === SignupMachineError.NoAddictionSelected,
      shouldSkipOnboardingCall: (context) =>
        isTobaccoLearn(context.selectedAddiction) ||
        (import.meta.env.DEV &&
          import.meta.env.VITE_SKIP_ONBOARDING_CALL === 'true'),
    },
  }
)

export default signupMachine
